import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Select, Spin, Tag, theme } from "antd";
import { fetchCanonicalPartOptions, matchingPartAlias } from "../../models/shared/canonicalPartsModel";
import { hasExactName } from '../../../../features/catalogue/models/cataloguePhase2Model';
import { useMissingCatalogueRequest } from '../../../../features/catalogue/viewmodels/useMissingCatalogueRequest';

export function CanonicalPartSelect({
  value,
  legacyLabel,
  onChange,
  onAddLegacy,
  queryPk,
  queryPartId,
  onRequested,
  size = "small",
  style,
  placeholder = "Part name *",
  ariaLabel = "Part name",
}) {
  const { token } = theme.useToken();
  const missing = useMissingCatalogueRequest('part', { query_pk: queryPk, query_part_id: queryPartId }, onRequested);
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [loadedSearch, setLoadedSearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [revision, setRevision] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const current = ++generation.current;
    const timer = setTimeout(() => {
      setLoading(true);
      setError(null);
      fetchCanonicalPartOptions({ search, includeId: value, limit: 100 })
        .then(rows => { if (current === generation.current) { setOptions(rows); setLoadedSearch(search); } })
        .catch(err => { if (current === generation.current) setError(err?.response?.data?.message || err?.message || "Could not load standard parts."); })
        .finally(() => { if (current === generation.current) setLoading(false); });
    }, search ? 300 : 0);
    return () => { clearTimeout(timer); generation.current += 1; };
  }, [search, value, revision]);

  const legacyValue = value == null && legacyLabel ? `legacy:${legacyLabel}` : undefined;
  const selectOptions = useMemo(() => [
    ...(legacyValue ? [{ value: legacyValue, label: legacyLabel, title: "Historical legacy part", disabled: true }] : []),
    ...options,
  ], [legacyLabel, legacyValue, options]);
  const exactExists = hasExactName(options, search);

  return (
    <div style={{ ...style, minWidth: 0 }}><Select
      showSearch
      filterOption={false}
      size={size}
      style={{ width: '100%' }}
      aria-label={ariaLabel}
      placeholder={placeholder}
      value={value ?? legacyValue}
      options={selectOptions}
      loading={loading}
      onSearch={setSearch}
      onOpenChange={open => { if (open) setRevision(current => current + 1); }}
      onClear={() => { setSearch(""); onChange?.(null, null, null); }}
      allowClear
      onChange={(selected, option) => {
        if (typeof selected !== "number") return;
        const canonical = options.find(item => item.id === selected) || option;
        onChange?.(selected, canonical, matchingPartAlias(canonical, search));
        setSearch("");
      }}
      notFoundContent={loading ? <Spin size="small" /> : (error || "No standard parts found")}
      popupRender={menu => (
        <>
          {menu}
          {error && <div style={{ padding: "6px 12px", color: token.colorError, fontSize: 12 }}>{error}</div>}
          {onAddLegacy && !error && !loading && loadedSearch === search && search.trim() && !exactExists && (
            <Button type="text" loading={missing.busy} style={{ height: 'auto', whiteSpace: 'normal', padding: '7px 12px', color: token.colorWarning }}
              onMouseDown={event => event.preventDefault()} onClick={() => { const text = search.trim(); missing.start(text, onAddLegacy); setSearch(''); }}>
              Missing part? Use “{search.trim()}” for now and tell an admin
            </Button>
          )}
        </>
      )}
    />{missing.row && value == null && legacyLabel === missing.row.typed_text && <Tag>Waiting for admin</Tag>}{missing.error && <Alert type="warning" message={missing.error} action={<Button size="small" onClick={missing.retry}>{missing.joinId ? 'Join existing request' : 'Retry admin request'}</Button>} />}</div>
  );
}
